import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/data/services";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

function errText(detail) {
  if (!detail) return "Sign in failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const { user, loading, login, verify2fa } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");

  useEffect(() => {
    if (!loading && user) navigate("/admin/dashboard");
  }, [user, loading, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await login(email.trim(), password);
      if (res?.twofa_required) {
        setChallenge(res.challenge);
        toast.success("We emailed you a 6-digit sign-in code.");
      } else {
        navigate("/admin/dashboard");
      }
    } catch (err) {
      toast.error(errText(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await verify2fa(challenge, code.trim());
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(errText(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#06182b] flex items-center justify-center px-5">
      <div className="relative z-10 w-full max-w-md">
        <button data-testid="admin-login-back" onClick={() => navigate("/")}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft size={16} /> Back to website
        </button>
        <div className="rounded-lg bg-white/5 border border-white/10 shadow-lg p-10">
          <div className="bg-brand-dark rounded-lg p-4 inline-flex mb-8">
            <img src={LOGO_URL} alt="Tanz-Bridge" className="h-14 object-contain rounded-md bg-white" />
          </div>
          <h1 className="text-2xl font-heading mb-2 text-white">Admin Dashboard</h1>
          <p className="text-gray-500 text-sm mb-8">
            Sign in to monitor subscriptions, client requests and payments.
          </p>

          <form onSubmit={challenge ? onVerify : onSubmit} className="space-y-4 text-left">
            {!challenge ? (
              <>
                <div>
                  <label className="text-xs uppercase tracking-wider text-gray-400 mb-1.5 block">Email</label>
                  <Input
                    data-testid="admin-login-email"
                    type="email"
                    required
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@tanzbridge.com"
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-gray-400 mb-1.5 block">Password</label>
                  <Input
                    data-testid="admin-login-password"
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  />
                </div>
                <Button data-testid="admin-login-submit" type="submit" disabled={submitting}
                  className="w-full bg-brand-blue text-white hover:bg-brand-bluehover rounded-sm py-6 text-base">
                  {submitting ? <Loader2 className="animate-spin" size={18} /> : "Sign in"}
                </Button>
              </>
            ) : (
              <>
                <div>
                  <label className="text-xs uppercase tracking-wider text-gray-400 mb-1.5 block">6-digit code (sent to your email)</label>
                  <Input
                    data-testid="admin-2fa-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="123456"
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 tracking-[0.4em] text-center text-lg"
                  />
                </div>
                <Button data-testid="admin-2fa-submit" type="submit" disabled={submitting}
                  className="w-full bg-brand-blue text-white hover:bg-brand-bluehover rounded-sm py-6 text-base">
                  {submitting ? <Loader2 className="animate-spin" size={18} /> : "Verify & sign in"}
                </Button>
                <button type="button" data-testid="admin-2fa-back" onClick={() => { setChallenge(null); setCode(""); }}
                  className="w-full text-xs text-gray-400 hover:text-white transition-colors">
                  Back to sign in
                </button>
              </>
            )}
          </form>

          <p className="text-xs text-gray-400 mt-6 flex items-center justify-center gap-1">
            <ShieldCheck size={13} /> Restricted to authorized Tanz-Bridge staff only.
          </p>
        </div>
      </div>
    </div>
  );
}
